import { Controller } from "@hotwired/stimulus"

// Opens and closes the native <details> of a FAQ accordion (Ui::Visitor::FaqAccordionComponent) with a height
// and fade animation instead of the instant jump. Without JS, or with reduced motion, <details> toggles as usual.
export default class extends Controller {
  static values = { duration: { type: Number, default: 320 } }

  connect () {
    this.animations = new Map()
  }

  disconnect () {
    this.animations.forEach((animation) => animation.cancel())
  }

  toggle (event) {
    const details = event.currentTarget.closest("details")
    if (!details || this.reducedMotion()) return

    event.preventDefault()
    if (details.open && details.dataset.state !== "closing") {
      this.close(details)
    } else {
      this.open(details)
    }
  }

  open (details) {
    const startHeight = this.stop(details)
    details.open = true
    details.dataset.state = "opening"
    const endHeight = details.offsetHeight

    this.animate(details, startHeight, endHeight, true)
    details.querySelector(":scope > :not(summary)")?.animate(
      { opacity: [0, 1], transform: ["translateY(-0.25rem)", "none"] },
      { duration: this.durationValue, easing: "ease-out" }
    )
  }

  close (details) {
    const startHeight = this.stop(details)
    details.open = false
    const endHeight = details.offsetHeight
    details.open = true
    details.dataset.state = "closing"

    this.animate(details, startHeight, endHeight, false)
  }

  // Cancels a running animation and returns the height it had reached, so a quick double click reverses smoothly.
  stop (details) {
    const height = details.offsetHeight
    this.animations.get(details)?.cancel()
    return height
  }

  animate (details, startHeight, endHeight, open) {
    details.style.overflow = "hidden"
    const animation = details.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      { duration: this.durationValue, easing: "cubic-bezier(0.4, 0, 0.2, 1)" }
    )
    this.animations.set(details, animation)

    animation.onfinish = () => this.finish(details, open)
    animation.oncancel = () => this.animations.delete(details)
  }

  finish (details, open) {
    details.open = open
    delete details.dataset.state
    details.style.overflow = ""
    this.animations.delete(details)
  }

  reducedMotion () {
    return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }
}
